import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GeometricBackground } from '../components/GeometricBackground';
import { KhatimStarLogo } from '../components/AalimLogo';
import { RamadanTopBanner, CharityInfoBox } from '../components/PromoBanner';
import { 
    MessageSquare, 
    Mic, 
    BookOpen, 
    Crown, 
    Star, 
    Check,
    ArrowRight,
    Volume2,
    GraduationCap,
    Heart,
    Library,
    ScrollText,
    Scale,
    History,
    Sparkles,
    Mail,
    HelpCircle,
    Briefcase
} from 'lucide-react';

const features = [
    {
        icon: MessageSquare,
        title: 'Islamic Q&A',
        description: 'Get answers to your Islamic questions based on Quran and authentic Hadith.',
        color: 'text-primary'
    },
    {
        icon: BookOpen,
        title: 'Authentic Sources',
        description: 'All answers are sourced from Quran, Sahih Hadith, and trusted scholars.',
        color: 'text-secondary'
    },
    {
        icon: Mic,
        title: 'Voice Learning',
        description: 'Practice Quran recitation with AI-powered feedback on your tajweed.',
        color: 'text-primary',
        premium: true
    },
    {
        icon: GraduationCap,
        title: 'Personalized Lessons',
        description: 'Custom lesson plans for Quran memorization and Islamic studies.',
        color: 'text-secondary',
        premium: true
    }
];

// Knowledge sources used by aalim.ai - generic descriptions (no website names)
const knowledgeSources = [
    {
        category: 'The Noble Quran',
        icon: BookOpen,
        sources: [
            { name: 'Complete Quran', description: 'Full Arabic text with translations' },
            { name: 'Tafsir Ibn Kathir', description: 'Classical scholarly interpretation' }
        ]
    },
    {
        category: 'Hadith Collections',
        icon: ScrollText,
        sources: [
            { name: 'Sahih al-Bukhari', description: 'Over 7,500 authenticated narrations' },
            { name: 'Sahih Muslim', description: 'Major authentic collection' },
            { name: 'Sunan Collections', description: 'Abu Dawud, Tirmidhi, Nasa\'i, Ibn Majah' }
        ]
    },
    {
        category: 'Fiqh & Fatwas',
        icon: Scale,
        sources: [
            { name: 'Scholarly Rulings', description: 'Verified fatwa database' },
            { name: 'Growing Daily', description: 'Continuously expanding collection' }
        ]
    },
    {
        category: 'Tajweed & Recitation',
        icon: Mic,
        sources: [
            { name: 'Al-Jazariyyah', description: 'Classical tajweed rules' },
            { name: '7 Qira\'at', description: 'Canonical recitation styles' }
        ]
    }
];

const pricingPlans = [
    {
        name: 'Free',
        price: '£0',
        period: 'forever',
        description: 'Perfect for casual learners',
        ustadhLabel: null,
        features: [
            'Unlimited Islamic Q&A',
            'Chat history',
            'Quran & Hadith references',
            'Basic source citations'
        ],
        cta: 'Get Started',
        highlight: false
    },
    {
        name: 'Premium',
        price: '£7.99',
        period: '/month',
        description: 'For serious students of knowledge',
        ustadhLabel: 'Ustadh Mode',
        features: [
            'Everything in Free',
            'Voice-enabled AI teacher',
            'Quran recitation practice',
            'Tajweed feedback & correction',
            'Personalized lesson plans',
            'Hifz progress tracking'
        ],
        cta: 'Start Learning',
        highlight: true
    },
    {
        name: 'Premium Yearly',
        price: '£75',
        period: '/year',
        description: 'Best value - 2 months free!',
        ustadhLabel: 'Ustadh Mode',
        features: [
            'Everything in Premium',
            'Priority support',
            'Early access to new features',
            'Save £21 per year'
        ],
        cta: 'Save 22%',
        highlight: false
    }
];

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-background">
            {/* Promo Banner - Uncomment when running promotions */}
            {/* <RamadanTopBanner /> */}
            
            {/* Hero Section with Black to Green Gradient */}
            <section className="relative min-h-screen flex items-start justify-center overflow-hidden">
                {/* Gradient Overlay - Dark mode only: Black at top fading to emerald green */}
                <div 
                    className="absolute inset-0 z-[1] dark:block hidden" 
                    style={{
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.6) 20%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.2) 60%, transparent 80%)'
                    }}
                />
                
                <GeometricBackground showAyatAlKursi={true} />
                
                <div className="relative z-10 max-w-6xl mx-auto px-6 pb-20 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {/* Logo - Large overlapping style */}
                        <div className="flex justify-center -mt-8 sm:-mt-16 -mb-8 sm:-mb-20 md:-mb-28 lg:-mb-36">
                            <KhatimStarLogo className="w-48 h-48 sm:w-80 sm:h-80 md:w-[500px] md:h-[500px] lg:w-[640px] lg:h-[640px]" />
                        </div>
                        
                        {/* Badge - positioned below the logo, not inside it */}
                        <span className="relative z-10 inline-block px-4 py-1.5 mb-4 sm:mb-6 text-xs sm:text-sm font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                            Your AI Islamic Knowledge Companion
                        </span>
                        
                        <h1 className="font-heading text-3xl sm:text-5xl md:text-7xl font-semibold tracking-tight text-foreground mb-4 sm:mb-6">
                            Seek Knowledge with
                            <span className="block text-secondary">aalim.ai</span>
                        </h1>
                        
                        <p className="text-sm sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-10">
                            An AI-powered platform for Muslims to explore Islamic knowledge, 
                            practice Quran recitation, and learn from authentic sources — 
                            the Quran and Sunnah.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/register">
                                <Button 
                                    size="lg" 
                                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground px-8 py-6 text-lg rounded-xl shadow-lg shadow-secondary/20"
                                    data-testid="hero-cta-start"
                                >
                                    Try for Free
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button 
                                    variant="outline" 
                                    size="lg"
                                    className="px-8 py-6 text-lg rounded-xl border-secondary/30 hover:border-secondary hover:text-secondary"
                                    data-testid="hero-cta-login"
                                >
                                    Login
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                    
                    {/* Hero Image/Demo Preview - Mimics actual chat interface */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="mt-16 relative"
                    >
                        <div className="glass-card rounded-2xl overflow-hidden max-w-4xl mx-auto border border-secondary/20 shadow-2xl shadow-secondary/5">
                            {/* Window Header */}
                            <div className="flex items-center gap-2 px-4 py-3 bg-card/50 border-b border-border">
                                <div className="w-3 h-3 rounded-full bg-destructive/80" />
                                <div className="w-3 h-3 rounded-full bg-secondary/80" />
                                <div className="w-3 h-3 rounded-full bg-primary/80" />
                                <span className="ml-4 text-sm text-muted-foreground">aalim.ai Chat</span>
                            </div>
                            
                            {/* Disclaimer Bar */}
                            <div className="bg-secondary/10 border-b border-secondary/20 px-4 py-2">
                                <div className="flex items-center gap-2 text-xs text-secondary">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <p>
                                        <strong>Please note:</strong> Aalim.AI cannot issue fatwas. For specific rulings, please consult your local imam.
                                    </p>
                                </div>
                            </div>
                            
                            {/* Chat Messages */}
                            <div className="p-6 space-y-4">
                                {/* User Message - no avatar */}
                                <div className="flex justify-end">
                                    <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-4 py-3 max-w-md">
                                        <p className="text-sm">What does the Quran say about patience?</p>
                                    </div>
                                </div>
                                
                                {/* Assistant Message - no avatar */}
                                <div className="flex justify-start">
                                    <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3 max-w-xl">
                                        <div className="text-sm space-y-2">
                                            <p className="font-arabic text-base leading-relaxed text-right" dir="rtl">
                                                يَا أَيُّهَا الَّذِينَ آمَنُوا اسْتَعِينُوا بِالصَّبْرِ وَالصَّلَاةِ ۚ إِنَّ اللَّهَ مَعَ الصَّابِرِينَ
                                            </p>
                                            <p>
                                                <strong>"O you who believe! Seek help through patience and prayer. Indeed, Allah is with the patient."</strong>
                                            </p>
                                            <p className="text-primary text-xs">(Surah Al-Baqarah 2:153)</p>
                                            <p className="text-muted-foreground mt-2">
                                                The Quran emphasizes patience (sabr) as a key virtue. This verse connects patience with prayer as twin pillars of spiritual strength...
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Input Area */}
                            <div className="px-6 pb-6">
                                <div className="flex gap-3 items-center bg-muted/50 rounded-xl px-4 py-3 border border-border">
                                    <input 
                                        type="text" 
                                        placeholder="Ask about Islam, Quran, or Hadith..." 
                                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                        disabled
                                    />
                                    <button className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                        <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-24 md:py-32 relative">
                <div className="max-w-6xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-secondary/10 text-secondary border border-secondary/20">
                            Features
                        </span>
                        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground mb-4">
                            Your Path to Islamic Knowledge
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Built with reverence for authentic Islamic scholarship and modern AI capabilities.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Card className="h-full bg-card border-border hover:border-primary/30 transition-colors group">
                                    <CardContent className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className={`w-12 h-12 rounded-xl ${feature.color === 'text-primary' ? 'bg-primary/10' : 'bg-secondary/10'} flex items-center justify-center shrink-0`}>
                                                <feature.icon className={`w-6 h-6 ${feature.color}`} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-heading text-xl font-semibold text-foreground">
                                                        {feature.title}
                                                    </h3>
                                                    {feature.premium && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-secondary/10 text-secondary">
                                                            <Crown className="w-3 h-3" />
                                                            Premium
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-muted-foreground">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    {/* Charity Info Box */}
                    <CharityInfoBox />

                    {/* Knowledge Sources Panel */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="mt-16"
                    >
                        <div className="text-center mb-8">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                                <Library className="w-4 h-4" />
                                Our Knowledge Base
                            </span>
                            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
                                Grounded in Authentic Scholarship
                            </h3>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                Every answer includes proper daleel (evidence) from the Quran, authenticated Hadith collections, and verified scholarly rulings.
                            </p>
                            <p className="text-sm text-secondary mt-3 font-medium">
                                Following the methodology of Ahlul Sunnah wal Jama'ah
                            </p>
                            <p className="text-xs text-primary/70 mt-2 flex items-center justify-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                Continuously growing database of verified Islamic knowledge
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {knowledgeSources.map((category, index) => (
                                <motion.div
                                    key={category.category}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                    <Card className="h-full bg-card/50 border-border hover:border-secondary/30 transition-all">
                                        <CardContent className="p-5">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                    <category.icon className="w-5 h-5 text-secondary" />
                                                </div>
                                                <h4 className="font-heading font-semibold text-foreground">
                                                    {category.category}
                                                </h4>
                                            </div>
                                            <div className="space-y-2">
                                                {category.sources.map((source, i) => (
                                                    <div key={i} className="flex items-start gap-2">
                                                        <Sparkles className="w-3 h-3 text-primary mt-1 shrink-0" />
                                                        <div>
                                                            <span className="text-sm font-medium text-foreground">{source.name}</span>
                                                            <p className="text-xs text-muted-foreground">{source.description}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Ustadh Mode Section */}
            <section className="py-24 md:py-32 relative bg-muted/30">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                        >
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-secondary/10 text-secondary border border-secondary/20">
                                <Crown className="w-4 h-4" />
                                Ustadh Mode
                            </span>
                            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground mb-6">
                                Your Personal Quran Teacher
                            </h2>
                            <p className="text-lg text-muted-foreground mb-8">
                                Experience AI-powered Quran education with voice interaction, 
                                tajweed feedback, and personalized lesson plans designed for your learning journey.
                            </p>
                            
                            <div className="space-y-4 mb-8">
                                {[
                                    { icon: Volume2, text: 'Voice-enabled conversations' },
                                    { icon: Mic, text: 'Real-time recitation feedback' },
                                    { icon: BookOpen, text: 'Tajweed rules from Al-Jazariyyah' },
                                    { icon: Heart, text: 'Hifz memorization tracking' }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
                                            <item.icon className="w-4 h-4 text-secondary" />
                                        </div>
                                        <span className="text-foreground">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                            
                            <Link to="/register">
                                <Button 
                                    size="lg" 
                                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                                    data-testid="ustadh-cta"
                                >
                                    Try Ustadh Mode
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="glass-card rounded-2xl p-6 border border-secondary/20">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                                        <GraduationCap className="w-5 h-5 text-secondary" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-foreground">Ustadh AI</h4>
                                        <p className="text-xs text-muted-foreground">Quran Teacher</p>
                                    </div>
                                </div>
                                
                                <div className="bg-background rounded-xl p-4 mb-4">
                                    <p className="text-sm text-foreground mb-2">
                                        MashaAllah! Your recitation of Surah Al-Fatiha is improving. 
                                        I noticed you're working on the ق (Qaf) pronunciation.
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Let's practice the makhraj together. The ق is pronounced from the back of the tongue...
                                    </p>
                                </div>
                                
                                {/* Voice Visualization */}
                                <div className="flex items-center justify-center gap-1 py-4">
                                    {[...Array(5)].map((_, i) => (
                                        <div 
                                            key={i} 
                                            className="voice-bar w-1 bg-secondary rounded-full"
                                            style={{ height: '16px' }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-24 md:py-32 relative">
                <div className="max-w-6xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-secondary/10 text-secondary border border-secondary/20">
                            Pricing
                        </span>
                        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground mb-4">
                            Start Your Learning Journey
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Begin with our free plan and upgrade when you're ready for deeper learning.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {pricingPlans.map((plan, index) => (
                            <motion.div
                                key={plan.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="relative"
                            >
                                {plan.highlight && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                                        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-secondary text-secondary-foreground">
                                            <Star className="w-3 h-3" />
                                            Most Popular
                                        </span>
                                    </div>
                                )}
                                <Card className={`h-full ${plan.highlight ? 'border-secondary tracing-beam' : 'border-border'}`}>
                                    <CardContent className="p-6">
                                        <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                                            {plan.name}
                                        </h3>
                                        <div className="mb-4">
                                            <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                                            <span className="text-muted-foreground">{plan.period}</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground mb-6">
                                            {plan.description}
                                        </p>
                                        
                                        {plan.ustadhLabel && (
                                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-secondary/20">
                                                <Crown className="w-4 h-4 text-secondary" />
                                                <span className="text-sm font-semibold text-secondary">{plan.ustadhLabel}</span>
                                            </div>
                                        )}
                                        
                                        <ul className="space-y-3 mb-6">
                                            {plan.features.map((feature, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm">
                                                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                                    <span className="text-foreground">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        
                                        <Link to="/register">
                                            <Button 
                                                className={`w-full ${plan.highlight ? 'bg-secondary hover:bg-secondary/90 text-secondary-foreground' : ''}`}
                                                variant={plan.highlight ? 'default' : 'outline'}
                                                data-testid={`pricing-cta-${plan.name.toLowerCase().replace(' ', '-')}`}
                                            >
                                                {plan.cta}
                                            </Button>
                                        </Link>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-16 border-t border-secondary/20">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                            <Mail className="w-4 h-4" />
                            Contact Us
                        </span>
                        <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
                            Get in Touch
                        </h3>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Have questions, feedback, or want to collaborate? We'd love to hear from you.
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Support */}
                        <Card className="border-border hover:border-primary/50 transition-colors">
                            <CardContent className="p-6 text-center">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <HelpCircle className="w-6 h-6 text-primary" />
                                </div>
                                <h4 className="font-heading font-semibold text-foreground mb-2">Support</h4>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Need help with your account or have technical issues?
                                </p>
                                <a 
                                    href="mailto:support@aalim.ai" 
                                    className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                                >
                                    support@aalim.ai
                                </a>
                            </CardContent>
                        </Card>
                        
                        {/* General Contact */}
                        <Card className="border-border hover:border-secondary/50 transition-colors">
                            <CardContent className="p-6 text-center">
                                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                                    <MessageSquare className="w-6 h-6 text-secondary" />
                                </div>
                                <h4 className="font-heading font-semibold text-foreground mb-2">Feedback</h4>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Have suggestions or found a bug? Let us know!
                                </p>
                                <a 
                                    href="mailto:contact@aalim.ai" 
                                    className="text-secondary hover:text-secondary/80 font-medium text-sm transition-colors"
                                >
                                    contact@aalim.ai
                                </a>
                            </CardContent>
                        </Card>
                        
                        {/* Business */}
                        <Card className="border-border hover:border-primary/50 transition-colors">
                            <CardContent className="p-6 text-center">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <Briefcase className="w-6 h-6 text-primary" />
                                </div>
                                <h4 className="font-heading font-semibold text-foreground mb-2">Business</h4>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Interested in partnerships, collaborations, or advertising?
                                </p>
                                <a 
                                    href="mailto:business@aalim.ai" 
                                    className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                                >
                                    business@aalim.ai
                                </a>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-secondary/20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center gap-1 mb-3">
                                <KhatimStarLogo size={36} />
                                <span className="font-heading text-lg font-semibold -ml-1">aalim<span className="text-secondary">.ai</span></span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Authentic Islamic knowledge, powered by AI.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-semibold text-foreground mb-3 text-sm">Product</h4>
                            <div className="space-y-2">
                                <Link to="/chat" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Free Chat</Link>
                                <Link to="/subscription" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Ustadh Mode</Link>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold text-foreground mb-3 text-sm">Company</h4>
                            <div className="space-y-2">
                                <Link to="/about" className="block text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-about-link">About</Link>
                                <Link to="/blogs" className="block text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-blog-link">Blog</Link>
                                <Link to="/terms" className="block text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms & Privacy</Link>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold text-foreground mb-3 text-sm">Contact</h4>
                            <div className="space-y-2">
                                <a href="mailto:contact@aalim.ai" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">contact@aalim.ai</a>
                                <a href="mailto:business@aalim.ai" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">business@aalim.ai</a>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-border pt-6 text-center text-sm text-muted-foreground">
                        &copy; 2026 aalim.ai &mdash; All answers sourced from Quran and authentic Sunnah.
                    </div>
                </div>
            </footer>
        </div>
    );
}
